import { useRef, type RefObject } from 'react';
import ReactMarkdown from 'react-markdown';
import { RotateCcw } from 'lucide-react';
import { Theory } from '../../types/world';
import { PanelResizeHandle } from '../common/PanelResizeHandle';

interface TheoryPanelProps {
  theory?: Theory;
  resizeWidthRef?: RefObject<HTMLElement>;
  onResize: (size: { width: number; height: number }) => void;
  onRestoreLayout: () => void;
}

export function TheoryPanel({ theory, resizeWidthRef, onResize, onRestoreLayout }: TheoryPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={panelRef} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col absolute inset-0 overflow-hidden">
      <div className="p-4 flex shrink-0 items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Theory</h3>
        <button type="button" onClick={onRestoreLayout} aria-label="Restore layout" title="Restore default panel sizes" className="inline-flex shrink-0 items-center gap-1.5 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-gray-200 dark:hover:bg-gray-600">
          <RotateCcw size={14} aria-hidden="true" /><span className="hidden sm:inline">Restore layout</span>
        </button>
      </div>
      
      {theory && (
        <div className="p-4 pb-6 border-t border-gray-200 dark:border-gray-700 overflow-y-auto flex-1 min-h-0">
          <div className="level-theory prose prose-sm max-w-none dark:prose-invert">
            <ReactMarkdown>{theory.markdown}</ReactMarkdown>
          </div>
          
          {theory.examples && theory.examples.length > 0 && (
            <div className="mt-4 space-y-3">
              {theory.examples.map((example, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 rounded p-3">
                  <h5 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">
                    {example.title}
                  </h5>
                  <pre className="text-xs font-mono text-gray-800 dark:text-gray-200 overflow-x-auto">
                    <code>{example.code}</code>
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {!theory && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 overflow-y-auto flex-1">
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No theory content available for this level.</p>
        </div>
      )}
      <PanelResizeHandle label="Resize theory panel" panelRef={panelRef} widthRef={resizeWidthRef} onResize={onResize} />
    </div>
  );
}
